import React, { useState, useEffect, useMemo } from 'react';
import { 
  Clock, 
  LogIn, 
  LogOut, 
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Timer,
  TrendingUp,
  CalendarDays,
  History,
  MapPin,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Grid3X3,
  List,
  Sun,
  Moon,
  Coffee,
  Zap,
  Award,
  Target,
  Flame
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import { db } from '@/lib/db';
import { v4 as uuidv4 } from 'uuid';

export default function AttendancePage() {
  const { user } = useAuthStore();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayRecord, setTodayRecord] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'list', 'stats'
  const [selectedDate, setSelectedDate] = useState(null);
  const [streak, setStreak] = useState(0);
  const [stats, setStats] = useState({
    totalDays: 0,
    presentDays: 0,
    absentDays: 0,
    lateDays: 0,
    avgWorkHours: 0,
    totalOvertime: 0,
    onTimeRate: 0,
    earlyBirdDays: 0,
    perfectWeeks: 0
  });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadAttendanceData();
  }, [user?.id, selectedMonth, selectedYear]);

  const loadAttendanceData = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const userId = user.id;
      
      // Get today's start timestamp
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = today.getTime();

      // Get all attendance records for this user
      const allUserRecords = await db.attendanceRecords
        .where('userId')
        .equals(userId)
        .toArray();

      // Get today's attendance record
      const todayRecords = allUserRecords.filter(record => {
        const recordDate = new Date(record.date);
        recordDate.setHours(0, 0, 0, 0);
        return recordDate.getTime() === todayTimestamp;
      });

      setTodayRecord(todayRecords[0] || null);

      // Get attendance history for selected month/year
      const startOfMonth = new Date(selectedYear, selectedMonth, 1);
      startOfMonth.setHours(0, 0, 0, 0);
      const startOfMonthTimestamp = startOfMonth.getTime();
      
      const endOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      const endOfMonthTimestamp = endOfMonth.getTime();

      const history = allUserRecords.filter(record => 
        record.date >= startOfMonthTimestamp && record.date <= endOfMonthTimestamp
      );

      // Sort by date descending
      history.sort((a, b) => b.date - a.date);
      setAttendanceHistory(history);

      // Calculate streak using already-fetched records
      let currentStreak = 0;
      const sortedRecords = [...allUserRecords].sort((a, b) => b.date - a.date);
      for (const record of sortedRecords) {
        if (record.status === 'present' && record.checkIn) {
          currentStreak++;
        } else {
          break;
        }
      }
      setStreak(currentStreak);

      // Calculate comprehensive stats
      const presentRecords = history.filter(r => r.status === 'present');
      const absentRecords = history.filter(r => r.status === 'absent');
      const lateRecords = history.filter(r => {
        if (!r.checkIn) return false;
        const checkInTime = new Date(r.checkIn);
        return checkInTime.getHours() > 9 || (checkInTime.getHours() === 9 && checkInTime.getMinutes() > 15);
      });
      const earlyBirdRecords = history.filter(r => {
        if (!r.checkIn) return false;
        const checkInTime = new Date(r.checkIn);
        return checkInTime.getHours() < 9;
      });

      const totalWorkHours = presentRecords.reduce((sum, r) => sum + (Number(r.workHours) || 0), 0);
      const totalOvertime = presentRecords.reduce((sum, r) => sum + (Number(r.overtime) || 0), 0);

      // Count working days in the month (excluding weekends)
      let workingDays = 0;
      const now = new Date();
      const lastDay = selectedMonth === now.getMonth() && selectedYear === now.getFullYear() 
        ? now.getDate() 
        : new Date(selectedYear, selectedMonth + 1, 0).getDate();
      
      for (let d = 1; d <= lastDay; d++) {
        const date = new Date(selectedYear, selectedMonth, d);
        if (date.getDay() !== 0 && date.getDay() !== 6) {
          workingDays++;
        }
      }

      // Calculate perfect weeks (5 consecutive weekdays present)
      let perfectWeeks = 0;
      // Simple approximation
      if (presentRecords.length >= 5) {
        perfectWeeks = Math.floor(presentRecords.length / 5);
      }

      const onTimeRecords = presentRecords.filter(r => {
        if (!r.checkIn) return false;
        const checkInTime = new Date(r.checkIn);
        return checkInTime.getHours() < 9 || (checkInTime.getHours() === 9 && checkInTime.getMinutes() <= 15);
      });

      setStats({
        totalDays: workingDays,
        presentDays: presentRecords.length,
        absentDays: absentRecords.length,
        lateDays: lateRecords.length,
        avgWorkHours: presentRecords.length > 0 ? (totalWorkHours / presentRecords.length).toFixed(1) : 0,
        totalOvertime: totalOvertime.toFixed(1),
        onTimeRate: presentRecords.length > 0 ? Math.round((onTimeRecords.length / presentRecords.length) * 100) : 0,
        earlyBirdDays: earlyBirdRecords.length,
        perfectWeeks
      });

    } catch (error) {
      console.error('Error loading attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!user) return;
    
    try {
      const now = new Date();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (todayRecord?.checkIn) {
        toast.error('You have already checked in today');
        return;
      }

      const checkInTime = now.getTime();
      const isLate = now.getHours() > 9 || (now.getHours() === 9 && now.getMinutes() > 15);

      if (todayRecord) {
        await db.attendanceRecords.update(todayRecord.id, {
          checkIn: checkInTime,
          status: 'present',
          updatedAt: Date.now()
        });
      } else {
        await db.attendanceRecords.add({
          id: uuidv4(),
          userId: user.id,
          date: today.getTime(),
          checkIn: checkInTime,
          checkOut: null,
          status: 'present',
          workHours: 0,
          overtime: 0,
          createdAt: Date.now()
        });
      }

      if (isLate) {
        toast.warning(`Checked in at ${formatTime(now)} - You're late!`);
      } else {
        toast.success(`Checked in successfully at ${formatTime(now)} 🎉`);
      }

      loadAttendanceData();
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error('Failed to check in');
    }
  };

  const handleCheckOut = async () => {
    if (!user || !todayRecord?.checkIn) {
      toast.error('Please check in first');
      return;
    }

    if (todayRecord?.checkOut) {
      toast.error('You have already checked out today');
      return;
    }

    try {
      const now = new Date();
      const checkOutTime = now.getTime();
      const checkInTime = new Date(todayRecord.checkIn);
      
      const workMs = checkOutTime - checkInTime;
      const workHours = workMs / (1000 * 60 * 60);
      const overtime = Math.max(0, workHours - 8);

      await db.attendanceRecords.update(todayRecord.id, {
        checkOut: checkOutTime,
        workHours: workHours.toFixed(2),
        overtime: overtime.toFixed(2),
        updatedAt: Date.now()
      });

      toast.success(`Checked out at ${formatTime(now)}. Worked ${workHours.toFixed(1)} hours.`);
      loadAttendanceData();
    } catch (error) {
      console.error('Check-out error:', error);
      toast.error('Failed to check out');
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateShort = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Generate calendar days for the selected month
  const calendarDays = useMemo(() => {
    const firstDay = new Date(selectedYear, selectedMonth, 1);
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty slots for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }
    
    // Add all days of the month
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(selectedYear, selectedMonth, d);
      const timestamp = date.getTime();
      const record = attendanceHistory.find(r => {
        const recordDate = new Date(r.date);
        recordDate.setHours(0, 0, 0, 0);
        return recordDate.getTime() === timestamp;
      });
      
      days.push({
        day: d,
        date: date,
        timestamp: timestamp,
        record: record,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: date.toDateString() === new Date().toDateString(),
        isFuture: date > new Date()
      });
    }
    
    return days;
  }, [selectedMonth, selectedYear, attendanceHistory]);

  const getAttendanceColor = (day) => {
    if (!day.day || day.isWeekend) return 'bg-gray-50';
    if (day.isFuture) return 'bg-gray-50 opacity-50';
    if (!day.record) return 'bg-red-50 border-red-200';
    
    if (day.record.status === 'present') {
      if (day.record.checkIn) {
        const checkInTime = new Date(day.record.checkIn);
        const isLate = checkInTime.getHours() > 9 || (checkInTime.getHours() === 9 && checkInTime.getMinutes() > 15);
        if (isLate) return 'bg-amber-50 border-amber-300';
        return 'bg-emerald-50 border-emerald-300';
      }
    }
    
    return 'bg-red-50 border-red-200';
  };

  const getAttendanceIcon = (day) => {
    if (!day.day || day.isWeekend || day.isFuture) return null;
    if (!day.record || day.record.status === 'absent') {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    
    if (day.record.status === 'present' && day.record.checkIn) {
      const checkInTime = new Date(day.record.checkIn);
      const isLate = checkInTime.getHours() > 9 || (checkInTime.getHours() === 9 && checkInTime.getMinutes() > 15);
      if (isLate) return <AlertCircle className="w-4 h-4 text-amber-500" />;
      return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    }
    
    return null;
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  const attendanceRate = stats.totalDays > 0 
    ? ((stats.presentDays / stats.totalDays) * 100).toFixed(1) 
    : 0;

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return { text: 'Good Morning', icon: Sun, color: 'text-amber-500' };
    if (hour < 17) return { text: 'Good Afternoon', icon: Coffee, color: 'text-orange-500' };
    return { text: 'Good Evening', icon: Moon, color: 'text-indigo-500' };
  };

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  return (
    <div className="space-y-6">
      {/* Header with Greeting */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500`}>
            <GreetingIcon className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              {greeting.text}, {user?.profile?.firstName || 'User'}! 
              {streak >= 5 && <Flame className="w-5 h-5 text-orange-500" />}
            </h1>
            <p className="text-gray-500 mt-0.5">
              {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {streak > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-lg border border-orange-200">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="font-semibold text-orange-700">{streak} Day Streak!</span>
            </div>
          )}
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
            <Clock className="h-5 w-5 text-gray-600" />
            <span className="font-mono text-lg font-semibold text-gray-800">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      {/* Check-in/Check-out Card */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold mb-1">Today's Attendance</h2>
              <p className="text-teal-100">
                {todayRecord?.checkIn 
                  ? `Checked in at ${formatTime(todayRecord.checkIn)}` 
                  : 'You haven\'t checked in yet'}
                {todayRecord?.checkOut && ` • Checked out at ${formatTime(todayRecord.checkOut)}`}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCheckIn}
                disabled={!!todayRecord?.checkIn}
                className={`px-6 py-3 ${
                  todayRecord?.checkIn 
                    ? 'bg-white/20 text-white/60 cursor-not-allowed' 
                    : 'bg-white text-teal-600 hover:bg-teal-50'
                }`}
              >
                <LogIn className="w-5 h-5 mr-2" />
                Check In
              </Button>
              <Button
                onClick={handleCheckOut}
                disabled={!todayRecord?.checkIn || !!todayRecord?.checkOut}
                className={`px-6 py-3 ${
                  !todayRecord?.checkIn || todayRecord?.checkOut
                    ? 'bg-white/20 text-white/60 cursor-not-allowed' 
                    : 'bg-white text-rose-600 hover:bg-rose-50'
                }`}
              >
                <LogOut className="w-5 h-5 mr-2" />
                Check Out
              </Button>
            </div>
          </div>
          
          {/* Today's Progress */}
          {todayRecord?.checkIn && !todayRecord?.checkOut && (
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span>Work Progress</span>
                <span>
                  {Math.min(100, Math.round(((Date.now() - todayRecord.checkIn) / (8 * 60 * 60 * 1000)) * 100))}% of 8 hours
                </span>
              </div>
              <Progress 
                value={Math.min(100, ((Date.now() - todayRecord.checkIn) / (8 * 60 * 60 * 1000)) * 100)} 
                className="h-2 bg-white/30"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Present</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.presentDays}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Absent</p>
                <p className="text-2xl font-bold text-red-600">{stats.absentDays}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Late Days</p>
                <p className="text-2xl font-bold text-amber-600">{stats.lateDays}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">On-Time Rate</p>
                <p className="text-2xl font-bold text-blue-600">{stats.onTimeRate}%</p>
              </div>
              <Target className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Avg Hours</p>
                <p className="text-2xl font-bold text-purple-600">{stats.avgWorkHours}</p>
              </div>
              <Timer className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Overtime</p>
                <p className="text-2xl font-bold text-indigo-600">{stats.totalOvertime}h</p>
              </div>
              <Zap className="w-8 h-8 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Rate Progress */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="font-semibold text-gray-900">Monthly Attendance Rate</h3>
              <p className="text-sm text-gray-500">
                {stats.presentDays} of {stats.totalDays} working days ({months[selectedMonth]} {selectedYear})
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Award className={`w-6 h-6 ${Number(attendanceRate) >= 95 ? 'text-amber-500' : 'text-gray-300'}`} />
              <span className="text-3xl font-bold text-gray-900">{attendanceRate}%</span>
            </div>
          </div>
          <Progress value={Number(attendanceRate)} className="h-3" />
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>0%</span>
            <span className={Number(attendanceRate) >= 95 ? 'text-emerald-600 font-semibold' : ''}>
              {Number(attendanceRate) >= 95 ? '🎉 Excellent!' : Number(attendanceRate) >= 80 ? 'Good' : 'Needs Improvement'}
            </span>
            <span>100%</span>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Area */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={() => navigateMonth('prev')}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center min-w-[150px]">
                <h2 className="text-lg font-semibold">{months[selectedMonth]} {selectedYear}</h2>
              </div>
              <Button variant="outline" size="icon" onClick={() => navigateMonth('next')}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Tabs value={viewMode} onValueChange={setViewMode}>
                <TabsList>
                  <TabsTrigger value="calendar" className="flex items-center gap-1">
                    <Grid3X3 className="w-4 h-4" />
                    Calendar
                  </TabsTrigger>
                  <TabsTrigger value="list" className="flex items-center gap-1">
                    <List className="w-4 h-4" />
                    List
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : viewMode === 'calendar' ? (
            <TooltipProvider>
              {/* Calendar View */}
              <div className="space-y-4">
                {/* Week Headers */}
                <div className="grid grid-cols-7 gap-2">
                  {weekDays.map(day => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {calendarDays.map((day, index) => (
                    <Tooltip key={index}>
                      <TooltipTrigger asChild>
                        <div
                          className={`
                            aspect-square p-2 rounded-lg border transition-all cursor-pointer
                            ${day.day ? 'hover:shadow-md' : ''}
                            ${day.isToday ? 'ring-2 ring-teal-500 ring-offset-2' : ''}
                            ${getAttendanceColor(day)}
                          `}
                          onClick={() => day.day && !day.isFuture && setSelectedDate(day)}
                        >
                          {day.day && (
                            <div className="h-full flex flex-col">
                              <span className={`text-sm font-medium ${day.isWeekend ? 'text-gray-400' : 'text-gray-700'}`}>
                                {day.day}
                              </span>
                              <div className="flex-1 flex items-center justify-center">
                                {getAttendanceIcon(day)}
                              </div>
                              {day.record?.workHours > 0 && (
                                <span className="text-xs text-gray-500 text-center">
                                  {Number(day.record.workHours).toFixed(1)}h
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </TooltipTrigger>
                      {day.day && !day.isWeekend && !day.isFuture && (
                        <TooltipContent>
                          <div className="text-sm">
                            <p className="font-semibold">{formatDateShort(day.timestamp)}</p>
                            {day.record ? (
                              <>
                                <p>Check In: {day.record.checkIn ? formatTime(day.record.checkIn) : 'N/A'}</p>
                                <p>Check Out: {day.record.checkOut ? formatTime(day.record.checkOut) : 'N/A'}</p>
                                <p>Hours: {day.record.workHours ? `${Number(day.record.workHours).toFixed(1)}h` : 'N/A'}</p>
                              </>
                            ) : (
                              <p className="text-red-500">Absent / No Record</p>
                            )}
                          </div>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  ))}
                </div>
                
                {/* Legend */}
                <div className="flex flex-wrap items-center gap-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-emerald-50 border border-emerald-300"></div>
                    <span className="text-sm text-gray-600">Present (On-time)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-amber-50 border border-amber-300"></div>
                    <span className="text-sm text-gray-600">Late</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-50 border border-red-200"></div>
                    <span className="text-sm text-gray-600">Absent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gray-50 border border-gray-200"></div>
                    <span className="text-sm text-gray-600">Weekend / Future</span>
                  </div>
                </div>
              </div>
            </TooltipProvider>
          ) : (
            /* List View */
            <div className="space-y-3">
              {attendanceHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No attendance records for this month</p>
                </div>
              ) : (
                attendanceHistory.map((record) => {
                  const isLate = record.checkIn && (
                    new Date(record.checkIn).getHours() > 9 || 
                    (new Date(record.checkIn).getHours() === 9 && new Date(record.checkIn).getMinutes() > 15)
                  );
                  
                  return (
                    <div 
                      key={record.id}
                      className={`
                        p-4 rounded-lg border flex flex-col md:flex-row md:items-center justify-between gap-4
                        ${record.status === 'present' 
                          ? isLate ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
                          : 'bg-red-50 border-red-200'
                        }
                      `}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`
                          w-12 h-12 rounded-lg flex items-center justify-center
                          ${record.status === 'present' 
                            ? isLate ? 'bg-amber-100' : 'bg-emerald-100'
                            : 'bg-red-100'
                          }
                        `}>
                          {record.status === 'present' 
                            ? isLate 
                              ? <AlertCircle className="w-6 h-6 text-amber-600" />
                              : <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                            : <XCircle className="w-6 h-6 text-red-600" />
                          }
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{formatDateShort(record.date)}</p>
                          <p className="text-sm text-gray-500">
                            {record.status === 'present' 
                              ? isLate ? 'Late Arrival' : 'Present'
                              : 'Absent'
                            }
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="text-gray-500">Check In</p>
                          <p className="font-semibold text-emerald-600">
                            {record.checkIn ? formatTime(record.checkIn) : '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-gray-500">Check Out</p>
                          <p className="font-semibold text-rose-600">
                            {record.checkOut ? formatTime(record.checkOut) : '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-gray-500">Work Hours</p>
                          <p className="font-semibold text-gray-900">
                            {record.workHours ? `${Number(record.workHours).toFixed(1)}h` : '-'}
                          </p>
                        </div>
                        {Number(record.overtime) > 0 && (
                          <div className="text-center">
                            <p className="text-gray-500">Overtime</p>
                            <p className="font-semibold text-indigo-600">
                              +{Number(record.overtime).toFixed(1)}h
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attendance Policy */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-gray-500 mt-0.5" />
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-700 mb-2">Attendance Policy</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  Standard hours: 9:00 AM - 6:00 PM
                </li>
                <li className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Late after 9:15 AM
                </li>
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-indigo-500" />
                  Overtime after 8 hours
                </li>
                <li className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-500" />
                  95%+ attendance = Excellent rating
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
